import { ChangeEventHandler, FocusEventHandler, ReactNode, Ref } from 'react';

import { useUniqueId } from 'utils/hooks';

import {
  InputAffixLabel,
  InputContainer,
  InputElement,
  InputPrefixVariant,
  InputStatus,
  InputStyleVariant,
} from './Input.styled';

type InputProps = {
  id?: string;
  name?: string;
  ref?: Ref<HTMLInputElement>;
  title?: string;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  qaId?: string;
  ariaLabel?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
};

type Props = {
  inputProps: InputProps;
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: InputStatus;
  prefixVariant?: InputPrefixVariant;
  styleVariant?: InputStyleVariant;
  fullHeight?: boolean;
};

export function Input({
  inputProps,
  prefix,
  suffix,
  status = 'neutral',
  prefixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
}: Props) {
  const inputId = useUniqueId({
    prefix: 'input-group-',
    override: inputProps.id,
  });

  return (
    <InputContainer
      $status={status}
      $prefixVariant={prefixVariant}
      $styleVariant={styleVariant}
      $fullHeight={Boolean(fullHeight)}
    >
      {prefix && <InputAffixLabel htmlFor={inputId}>{prefix}</InputAffixLabel>}

      <InputElement
        id={inputId}
        name={inputProps.name}
        ref={inputProps.ref}
        title={inputProps.title}
        placeholder={inputProps.placeholder}
        value={inputProps.value}
        onChange={inputProps.onChange}
        onBlur={inputProps.onBlur}
        onFocus={inputProps.onFocus}
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        autoFocus={inputProps.autoFocus}
        data-qa-id={inputProps.qaId}
        aria-label={inputProps.ariaLabel}
      />

      {suffix && <InputAffixLabel htmlFor={inputId}>{suffix}</InputAffixLabel>}
    </InputContainer>
  );
}
